import React, { memo, useState, useRef, useEffect } from 'react';
import { Type, ImageIcon, User, Settings, Upload, Clock, MapPin, Eye, Mail, Link2, Video, HelpCircle, Plus, X, CheckCircle, Star, AlertCircle } from 'lucide-react';
import { validateField } from '../utils/validators';
import { 
  validateImageFile, 
  validateImageDimensions, 
  getImagePreview,
  checkImageDimensions,
  IMAGE_REQUIREMENTS 
} from '../utils/imageValidation';
import CollapsibleSection from './CollapsibleSection';

// Import Event Setup components
import EditInfo from '../../../Events/EditInfo';
import EditTime from '../../../Events/EditTime';
import EventFormat from '../../../Events/EventFormat';
import MapVenue from '../../../Events/MapVenue';
import EventVisibility from '../../../Events/EventVisibility';
import EventEmail from '../../../Events/EventEmail';
import RedirectionLink from '../../../Events/RedirectionLink';

// Import Event Branding components
import EditVideoLink from '../../../Events/EditVideoLink';
import Faq from '../../../Events/Faq';
import Sponsors from '../../Participants/Sponsors/Sponsors';

/**
 * Image Upload Field Component with Validation & Backend Upload
 */
const ImageUploadField = memo(({ 
  label, 
  type = 'logo',
  value, 
  onChange, 
  onFileUpload,
  requirements,
  hint,
  token,
  eventID,
  isUploading: parentIsUploading,
  uploadProgress: parentUploadProgress
}) => {
  const [preview, setPreview] = useState(value || null);
  const [errors, setErrors] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const fileRef = useRef(null);

  const effectiveIsUploading = parentIsUploading !== undefined ? parentIsUploading : isUploading;
  const effectiveUploadProgress = parentUploadProgress !== undefined ? parentUploadProgress : uploadProgress;

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setErrors([]);
    setUploadProgress(0);

    try {
      // Validate file
      const fileErrors = validateImageFile(file, type);
      
      if (fileErrors.length > 0) {
        setErrors(fileErrors);
        setIsUploading(false);
        return;
      }

      // Check dimensions
      const dimensions = await checkImageDimensions(file);
      const dimensionErrors = validateImageDimensions(
        { width: dimensions.width, height: dimensions.height }, 
        type
      );
      
      if (dimensionErrors.length > 0) {
        setErrors([
          ...fileErrors,
          ...dimensionErrors,
          'Image will be resized automatically, but quality may be affected'
        ]);
      }

      // Create preview for immediate display
      const previewUrl = await getImagePreview(file);
      setPreview(previewUrl);
      
      // Store File object for backend upload
      fileRef.current = file;
      
      // Update parent with preview (for immediate UI feedback)
      onChange(previewUrl);
      
      // Trigger backend upload if callback provided
      if (onFileUpload && token && eventID) {
        await onFileUpload(file, type, (progress) => setUploadProgress(progress));
      }
      
    } catch (error) {
      console.error('Image upload error:', error);
      setErrors(['Failed to process image. Please try again.']);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    if (preview?.startsWith('blob:')) {
      URL.revokeObjectURL(preview);
    }
    setPreview(null);
    setErrors([]);
    fileRef.current = null;
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const req = requirements || IMAGE_REQUIREMENTS[type];

  return (
    <div className="landing-form-group">
      <label className="landing-label">{label}</label>
      
      <div className="landing-upload-area">
        {!preview ? (
          <>
            <div 
              className="landing-upload-placeholder"
              onClick={() => fileInputRef.current?.click()}
            >
              <ImageIcon size={32} className="landing-upload-icon" />
              <span>Click to upload {label.toLowerCase()}</span>
              <small className="landing-upload-requirements">
                {req?.maxWidth}×{req?.maxHeight}px • Max {req?.maxSizeMB}MB • JPG/PNG/WebP
              </small>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={req?.allowedTypes?.join(',')}
              onChange={handleFileSelect}
              disabled={effectiveIsUploading}
              style={{ display: 'none' }}
            />
            <button
              className="landing-button landing-button-primary landing-button-sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={effectiveIsUploading}
            >
              {effectiveIsUploading ? `Uploading ${effectiveUploadProgress}%...` : 'Choose File'}
            </button>
          </>
        ) : (
          <div className="landing-upload-preview">
            <img 
              src={preview} 
              alt={`${label} preview`} 
              className="landing-upload-image"
            />
            {effectiveIsUploading && (
              <div style={{
                position: 'absolute',
                bottom: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '80%',
                height: '4px',
                backgroundColor: 'rgba(0,0,0,0.1)',
                borderRadius: '2px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${effectiveUploadProgress}%`,
                  height: '100%',
                  backgroundColor: '#f68a20',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            )}
            <button
              className="landing-button landing-button-danger landing-button-sm"
              onClick={handleRemove}
              disabled={effectiveIsUploading}
            >
              <X size={14} />
              Remove
            </button>
          </div>
        )}
      </div>

      {/* Error Messages */}
      {errors.length > 0 && (
        <div className="landing-upload-error">
          <AlertCircle size={14} className="landing-error-icon" />
          <ul className="landing-error-list">
            {errors.map((error, idx) => (
              <li key={idx}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {hint && !errors.length && (
        <small className="landing-hint">{hint}</small>
      )}
    </div>
  );
});

ImageUploadField.displayName = 'ImageUploadField';

/**
 * Customize panel component for editing event data
 */
const CustomizePanel = memo(({ eventData = {}, onUpdate, errors = {}, onFieldBlur, eventDetails, token, userID }) => {
  // Ensure eventData is always an object
  const safeEventData = eventData || {};
  const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);
  const [isSponsorsModalOpen, setIsSponsorsModalOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  
  // Track uploaded files for batch upload on save
  const pendingUploadsRef = useRef({});
  const fileRefsRef = useRef({});

  const handleInputChange = (field, value) => {
    onUpdate(field, value);
    // Clear error when user starts typing
    if (errors[field]) {
      onFieldBlur?.(field, value);
    }
  };

  // Upload image to backend and return URL
  const uploadImageToBackend = async (file, imageType, onProgress) => {
    if (!token || !eventDetails?._id) {
      console.warn('Missing token or event ID for image upload');
      return null;
    }

    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('type', imageType); // 'logo' or 'banner'
      formData.append('eventId', eventDetails._id);

      // Replace with your actual upload endpoint
      const response = await fetch(`${process.env.REACT_APP_API}upload/event-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Upload failed');
      }

      const result = await response.json();
      return result.imageUrl; // Backend should return the saved image URL
      
    } catch (error) {
      console.error('Image upload to backend failed:', error);
      throw error;
    }
  };

  // Handle file selection with backend upload capability
  const handleFileChange = async (field, previewUrl, file) => {
    // Store preview for immediate UI update
    onUpdate(field, previewUrl);
    
    // Store file reference for later upload
    if (file) {
      fileRefsRef.current[field] = file;
    }
    
    // Mark for backend upload on save
    pendingUploadsRef.current[field] = { previewUrl, file };
  };

  // Save all pending uploads when user clicks Save
  const handleSavePendingUploads = async () => {
    const uploads = { ...pendingUploadsRef.current };
    pendingUploadsRef.current = {}; // Clear pending uploads
    
    const results = {};
    
    for (const [field, { previewUrl, file }] of Object.entries(uploads)) {
      try {
        setUploadStatus(prev => ({ ...prev, [field]: 'uploading' }));
        
        const imageUrl = await uploadImageToBackend(
          file, 
          field === 'bannerImage' ? 'banner' : 'logo',
          (progress) => {
            setUploadStatus(prev => ({ ...prev, [field]: `uploading-${progress}` }));
          }
        );
        
        if (imageUrl) {
          // Update with the permanent URL from backend
          onUpdate(field, imageUrl);
          results[field] = { success: true, url: imageUrl };
          setUploadStatus(prev => ({ ...prev, [field]: 'success' }));
        } else {
          // Keep the preview as fallback
          onUpdate(field, previewUrl);
          results[field] = { success: false, error: 'No URL returned' };
        }
      } catch (error) {
        console.error(`Failed to upload ${field}:`, error);
        // Keep the preview as fallback
        onUpdate(field, previewUrl);
        results[field] = { success: false, error: error.message };
        setUploadStatus(prev => ({ ...prev, [field]: 'error' }));
      }
    }
    
    return results;
  };

  // Expose save function to parent via effect
  useEffect(() => {
    window.__customizePanelSave = handleSavePendingUploads;
    return () => {
      delete window.__customizePanelSave;
    };
  }, []);

  const getCharacterCount = (text, maxLength) => {
    if (!text) return 0;
    return text.length;
  };

  // Features management for "What to Expect" section
  const features = Array.isArray(safeEventData.features) ? safeEventData.features : [];
  const [newFeature, setNewFeature] = useState('');

  // Agenda management - simple time-based agenda for in-person events
  const agenda = Array.isArray(safeEventData.agenda)
    ? safeEventData.agenda.map((item) => {
        // Backward compatibility: if old `time` field exists, treat it as startTime
        if (!item) return item;
        if (!item.startTime && item.time) {
          return { ...item, startTime: item.time };
        }
        return item;
      })
    : [];
  const [newAgendaItem, setNewAgendaItem] = useState({
    startTime: '',
    endTime: '',
    title: '',
    description: '',
  });

  const handleAddFeature = () => {
    if (newFeature.trim() && !features.includes(newFeature.trim())) {
      const updatedFeatures = [...features, newFeature.trim()];
      handleInputChange('features', updatedFeatures);
      setNewFeature('');
    }
  };

  const handleRemoveFeature = (index) => {
    const updatedFeatures = features.filter((_, i) => i !== index);
    handleInputChange('features', updatedFeatures);
  };

  const handleUpdateFeature = (index, newValue) => {
    if (newValue.trim()) {
      const updatedFeatures = [...features];
      updatedFeatures[index] = newValue.trim();
      handleInputChange('features', updatedFeatures);
    }
  };

  const handleAgendaItemChange = (index, field, value) => {
    const updatedAgenda = [...agenda];
    updatedAgenda[index] = {
      ...updatedAgenda[index],
      [field]: value,
    };
    handleInputChange('agenda', updatedAgenda);
  };

  const handleAddAgendaItem = () => {
    const trimmedTitle = newAgendaItem.title.trim();
    const trimmedStart = newAgendaItem.startTime.trim();
    const trimmedEnd = newAgendaItem.endTime.trim();
    if (!trimmedTitle || !trimmedStart || !trimmedEnd) return;

    const updatedAgenda = [
      ...agenda,
      {
        startTime: trimmedStart,
        endTime: trimmedEnd,
        title: trimmedTitle,
        description: newAgendaItem.description.trim(),
      },
    ];
    handleInputChange('agenda', updatedAgenda);
    setNewAgendaItem({ startTime: '', endTime: '', title: '', description: '' });
  };

  const handleRemoveAgendaItem = (index) => {
    const updatedAgenda = agenda.filter((_, i) => i !== index);
    handleInputChange('agenda', updatedAgenda);
  };

  const handleMoveAgendaItem = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= agenda.length) return;
    const updatedAgenda = [...agenda];
    const [movedItem] = updatedAgenda.splice(index, 1);
    updatedAgenda.splice(newIndex, 0, movedItem);
    handleInputChange('agenda', updatedAgenda);
  };

  return (
    <div className="landing-edit-panel">
      {/* Quick Edit Section - Most Common Fields */}
      <div className="landing-edit-section landing-section-primary">
        <div className="landing-section-header">
          <div className="landing-section-header-content">
            <Type size={20} className="landing-section-icon" />
            <div>
              <h4 className="landing-section-title">Event Information</h4>
              <p className="landing-section-description">Basic details about your event</p>
            </div>
          </div>
        </div>
        
        <div className="landing-form-group">
          <label className="landing-label">
            <span>Event Title</span>
            <span className="required">*</span>
            {safeEventData.title && (
              <span className="character-count">
                {getCharacterCount(safeEventData.title, 100)}/100
              </span>
            )}
          </label>
          <input
            type="text"
            className={`landing-input ${errors.title ? 'error' : ''}`}
            value={safeEventData.title || ''}
            onChange={(e) => handleInputChange('title', e.target.value)}
            onBlur={(e) => onFieldBlur?.('title', e.target.value)}
            maxLength={100}
            placeholder="Enter your event title"
            aria-invalid={!!errors.title}
            aria-describedby={errors.title ? 'title-error' : undefined}
          />
          {errors.title && (
            <span id="title-error" className="error-message" role="alert">
              {errors.title}
            </span>
          )}
          {!errors.title && (
            <small className="landing-hint">This will be the main heading on your landing page</small>
          )}
        </div>

        <div className="landing-form-group">
          <label className="landing-label">
            <span>Subtitle</span>
            {safeEventData.subtitle && (
              <span className="character-count">
                {getCharacterCount(safeEventData.subtitle, 60)}/60
              </span>
            )}
          </label>
          <input
            type="text"
            className="landing-input"
            value={safeEventData.subtitle || ''}
            onChange={(e) => handleInputChange('subtitle', e.target.value)}
            maxLength={60}
            placeholder="A short tagline or subtitle"
          />
          <small className="landing-hint">A brief tagline that appears below the title</small>
        </div>

        <div className="landing-form-group">
          <label className="landing-label">
            <span>Description</span>
            {safeEventData.description && (
              <span className="character-count">
                {getCharacterCount(safeEventData.description, 1000)}/1000
              </span>
            )}
          </label>
          <textarea
            className={`landing-textarea ${errors.description ? 'error' : ''}`}
            value={safeEventData.description || ''}
            onChange={(e) => handleInputChange('description', e.target.value)}
            onBlur={(e) => onFieldBlur?.('description', e.target.value)}
            maxLength={1000}
            rows={5}
            placeholder="Describe what attendees can expect from your event..."
            aria-invalid={!!errors.description}
            aria-describedby={errors.description ? 'description-error' : undefined}
          />
          {errors.description && (
            <span id="description-error" className="error-message" role="alert">
              {errors.description}
            </span>
          )}
          {!errors.description && (
            <small className="landing-hint">Provide a detailed description of your event</small>
          )}
        </div>

        {/* ===== START & END DATE ROW ===== */}
        <div className="landing-form-row">
          <div className="landing-form-group">
            <label className="landing-label">
              <span>Start Date</span>
            </label>
            <input
              type="date"
              className={`landing-input ${errors.startDate ? 'error' : ''}`}
              value={safeEventData.startDate || ''}
              onChange={(e) => handleInputChange('startDate', e.target.value)}
              onBlur={(e) => onFieldBlur?.('startDate', e.target.value)}
              aria-invalid={!!errors.startDate}
              aria-describedby={errors.startDate ? 'startDate-error' : undefined}
            />
            {errors.startDate && (
              <span id="startDate-error" className="error-message" role="alert">
                {errors.startDate}
              </span>
            )}
            {!errors.startDate && (
              <small className="landing-hint">When your event starts</small>
            )}
          </div>

          <div className="landing-form-group">
            <label className="landing-label">
              <span>End Date</span>
            </label>
            <input
              type="date"
              className={`landing-input ${errors.endDate ? 'error' : ''}`}
              value={safeEventData.endDate || ''}
              onChange={(e) => handleInputChange('endDate', e.target.value)}
              onBlur={(e) => onFieldBlur?.('endDate', e.target.value)}
              aria-invalid={!!errors.endDate}
              aria-describedby={errors.endDate ? 'endDate-error' : undefined}
            />
            {errors.endDate && (
              <span id="endDate-error" className="error-message" role="alert">
                {errors.endDate}
              </span>
            )}
            {!errors.endDate && (
              <small className="landing-hint">When your event ends (optional for single-day events)</small>
            )}
          </div>
        </div>

        {/* ✅ DATE CHANGE WARNING */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '8px',
          padding: '12px 16px', background: '#fff7ed', border: '1px solid #fed7aa',
          borderLeft: '4px solid #f97316', borderRadius: '8px',
          fontSize: '13px', color: '#92400e', marginTop: '12px', marginBottom: '16px'
        }}>
          <span style={{ fontSize: '16px', lineHeight: 1 }}>⚠️</span>
          <p style={{ margin: 0, lineHeight: 1.4 }}>
            <strong>Note:</strong> Changing event dates may affect registrations, tickets, and sessions.
            Please review all details after updating.
          </p>
        </div>

        {/* ===== TIME ROW ===== */}
        <div className="landing-form-row">
          <div className="landing-form-group">
            <label className="landing-label"><span>Event Time</span></label>
            <input
              type="time"
              className={`landing-input ${errors.time ? 'error' : ''}`}
              value={safeEventData.time || ''}
              onChange={(e) => handleInputChange('time', e.target.value)}
              onBlur={(e) => onFieldBlur?.('time', e.target.value)}
              aria-invalid={!!errors.time}
            />
            {errors.time && <span className="error-message" role="alert">{errors.time}</span>}
            {!errors.time && <small className="landing-hint">Start time of your event</small>}
          </div>

          <div className="landing-form-group">
            <label className="landing-label"><span>End Time</span></label>
            <input
              type="time"
              className={`landing-input ${errors.endTime ? 'error' : ''}`}
              value={safeEventData.endTime || ''}
              onChange={(e) => handleInputChange('endTime', e.target.value)}
              onBlur={(e) => onFieldBlur?.('endTime', e.target.value)}
              aria-invalid={!!errors.endTime}
            />
            {errors.endTime && <span className="error-message" role="alert">{errors.endTime}</span>}
            {!errors.endTime && <small className="landing-hint">End time (optional)</small>}
          </div>
        </div>

        {/* ===== LOCATION & CAPACITY ROW ===== */}
        <div className="landing-form-row">
          <div className="landing-form-group">
            <label className="landing-label"><span>Location</span></label>
            <input
              type="text"
              className={`landing-input ${errors.location ? 'error' : ''}`}
              value={safeEventData.location || ''}
              onChange={(e) => handleInputChange('location', e.target.value)}
              onBlur={(e) => onFieldBlur?.('location', e.target.value)}
              placeholder="e.g., Convention Center, New York"
              aria-invalid={!!errors.location}
            />
            {errors.location && <span className="error-message" role="alert">{errors.location}</span>}
            {!errors.location && <small className="landing-hint">Where your event will be held</small>}
          </div>

          <div className="landing-form-group">
            <label className="landing-label"><span>Expected Capacity</span></label>
            <input
              type="text"
              className={`landing-input ${errors.capacity ? 'error' : ''}`}
              value={safeEventData.capacity || ''}
              onChange={(e) => handleInputChange('capacity', e.target.value)}
              onBlur={(e) => onFieldBlur?.('capacity', e.target.value)}
              placeholder="e.g., 500"
            />
            {errors.capacity && <span className="error-message" role="alert">{errors.capacity}</span>}
            {!errors.capacity && <small className="landing-hint">Number of expected attendees</small>}
          </div>
        </div>
      </div> {/* ✅ CLOSE the first landing-edit-section (Event Information) */}

{/* Agenda section - only relevant for in-person (non-virtual) events */}
{eventDetails?.eventFormat !== "Virtual" && (
  <div className="landing-edit-section landing-section-primary">
    <div className="landing-section-header">
      <div className="landing-section-header-content">
        <Clock size={20} className="landing-section-icon" />
        <div>
          <h4 className="landing-section-title">Event Agenda</h4>
          <p className="landing-section-description">
            Outline what happens during the day with simple time slots
          </p>
        </div>
      </div>
    </div>

    <div className="landing-form-group">
      {/* Existing Agenda Items */}
{agenda.length > 0 && (
  <div className="agenda-items-list" style={{ marginBottom: '20px' }}>
    <h5 style={{
      margin: '0 0 16px 0',
      fontSize: '14px',
      fontWeight: '600',
      color: '#475569',
    }}>
      Current Agenda Items ({agenda.length})
    </h5>
    
    {agenda.map((item, index) => (
      <div
        key={index}
        className="agenda-item-card"
        style={{
          position: 'relative',
          padding: '16px 50px 16px 16px',
          marginBottom: '12px',
          backgroundColor: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
        }}
      >
        {/* Action Buttons - Positioned lower to avoid overlap */}
        <div style={{
          position: 'absolute',
          top: '50px',
          right: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        }}>
          <button
            type="button"
            onClick={() => handleMoveAgendaItem(index, -1)}
            disabled={index === 0}
            title="Move up"
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              backgroundColor: index === 0 ? '#f1f5f9' : '#ffffff',
              color: index === 0 ? '#cbd5e1' : '#64748b',
              cursor: index === 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="18 15 12 9 6 15"></polyline>
            </svg>
          </button>
          <button
            type="button"
            onClick={() => handleMoveAgendaItem(index, 1)}
            disabled={index === agenda.length - 1}
            title="Move down"
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              backgroundColor: index === agenda.length - 1 ? '#f1f5f9' : '#ffffff',
              color: index === agenda.length - 1 ? '#cbd5e1' : '#64748b',
              cursor: index === agenda.length - 1 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          <button
            type="button"
            onClick={() => handleRemoveAgendaItem(index)}
            title="Delete item"
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '6px',
              border: '1px solid #fecaca',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Content */}
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '12px' }}>
          {/* Number Badge */}
          <div style={{
            width: '32px',
            height: '32px',
            backgroundColor: '#f97316',
            color: '#ffffff',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '600',
            fontSize: '14px',
            flexShrink: 0,
          }}>
            {index + 1}
          </div>

          {/* Time and Title */}
          <div style={{ flex: 1, display: 'flex', gap: '12px', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="time"
                value={item.startTime || ''}
                onChange={(e) => handleAgendaItemChange(index, 'startTime', e.target.value)}
                className="landing-input"
                style={{ width: '110px', padding: '8px', fontSize: '13px' }}
              />
              <span style={{ color: '#94a3b8', fontSize: '12px' }}>to</span>
              <input
                type="time"
                value={item.endTime || ''}
                onChange={(e) => handleAgendaItemChange(index, 'endTime', e.target.value)}
                className="landing-input"
                style={{ width: '110px', padding: '8px', fontSize: '13px' }}
              />
            </div>
            <input
              type="text"
              value={item.title || ''}
              onChange={(e) => handleAgendaItemChange(index, 'title', e.target.value)}
              className="landing-input"
              placeholder="Session title"
              style={{ flex: 1, padding: '8px', fontSize: '14px', fontWeight: '600' }}
            />
          </div>
        </div>

        {/* Description */}
        <div style={{ marginLeft: '48px' }}>
          <textarea
            value={item.description || ''}
            onChange={(e) => handleAgendaItemChange(index, 'description', e.target.value)}
            className="landing-input"
            placeholder="Add a brief description (optional)"
            rows={2}
            style={{ 
              width: 'calc(100% - 48px)', 
              fontSize: '13px', 
              padding: '8px',
              resize: 'vertical',
            }}
          />
        </div>
      </div>
    ))}
  </div>
)}

      {/* Add New Agenda Item Form */}
      <div className="agenda-add-form" style={{
        padding: '20px',
        backgroundColor: '#f8fafc',
        border: '2px dashed #cbd5e1',
        borderRadius: '12px',
        marginBottom: '16px',
      }}>
        <h5 style={{
          margin: '0 0 16px 0',
          fontSize: '14px',
          fontWeight: '600',
          color: '#475569',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <Plus size={16} style={{ color: '#f97316' }} />
          Add New Agenda Item
        </h5>
        
        <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '0 0 140px' }}>
            <label style={{
              display: 'block',
              fontSize: '12px',
              fontWeight: '500',
              color: '#64748b',
              marginBottom: '6px',
            }}>
              Start Time *
            </label>
            <input
              type="time"
              value={newAgendaItem.startTime}
              onChange={(e) => setNewAgendaItem((prev) => ({ ...prev, startTime: e.target.value }))}
              className="landing-input"
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '14px',
              }}
            />
          </div>
          
          <div style={{ position: 'relative', flex: '0 0 140px' }}>
            <label style={{
              display: 'block',
              fontSize: '12px',
              fontWeight: '500',
              color: '#64748b',
              marginBottom: '6px',
            }}>
              End Time *
            </label>
            <input
              type="time"
              value={newAgendaItem.endTime}
              onChange={(e) => setNewAgendaItem((prev) => ({ ...prev, endTime: e.target.value }))}
              className="landing-input"
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '14px',
              }}
            />
          </div>
          
          <div style={{ flex: 1, minWidth: '200px' }}>
            <label style={{
              display: 'block',
              fontSize: '12px',
              fontWeight: '500',
              color: '#64748b',
              marginBottom: '6px',
            }}>
              Session Title *
            </label>
            <input
              type="text"
              value={newAgendaItem.title}
              onChange={(e) => setNewAgendaItem((prev) => ({ ...prev, title: e.target.value }))}
              className="landing-input"
              placeholder="e.g., Registration & Welcome Coffee"
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '14px',
              }}
            />
          </div>
        </div>
        
        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: '500',
            color: '#64748b',
            marginBottom: '6px',
          }}>
            Description (Optional)
          </label>
          <textarea
            value={newAgendaItem.description}
            onChange={(e) => setNewAgendaItem((prev) => ({ ...prev, description: e.target.value }))}
            className="landing-input"
            placeholder="Add a brief description of this session"
            rows={2}
            style={{
              width: '100%',
              padding: '10px 12px',
              fontSize: '13px',
              resize: 'vertical',
            }}
          />
        </div>
        
        {(() => {
          const startFilled = (newAgendaItem.startTime || '').trim();
          const endFilled = (newAgendaItem.endTime || '').trim();
          const titleFilled = (newAgendaItem.title || '').trim();
          const canAdd = !!startFilled && !!endFilled && !!titleFilled;
          
          return (
            <button
              type="button"
              onClick={handleAddAgendaItem}
              disabled={!canAdd}
              className="landing-button landing-button-primary"
              style={{
                padding: '12px 24px',
                backgroundColor: canAdd ? '#f97316' : '#cbd5e1',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                cursor: canAdd ? 'pointer' : 'not-allowed',
                fontWeight: '600',
                fontSize: '14px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s',
                boxShadow: canAdd ? '0 2px 8px rgba(249, 115, 22, 0.3)' : 'none',
              }}
              onMouseEnter={(e) => {
                if (canAdd) {
                  e.currentTarget.style.backgroundColor = '#ea580c';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (canAdd) {
                  e.currentTarget.style.backgroundColor = '#f97316';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(249, 115, 22, 0.3)';
                }
              }}
            >
              <Plus size={18} />
              Add to Agenda
            </button>
          );
        })()}
      </div>
    </div>
  </div>
)}

      {/* ✅ UPDATED: Media Assets Section with Backend Upload */}
      <div className="landing-edit-section landing-section-primary">
        <div className="landing-section-header">
          <div className="landing-section-header-content">
            <ImageIcon size={20} className="landing-section-icon" />
            <div>
              <h4 className="landing-section-title">Media Assets</h4>
              <p className="landing-section-description">Upload images for your landing page</p>
            </div>
          </div>
        </div>
        
        <div className="landing-upload-grid">
          {/* Hero Banner Upload */}
          <ImageUploadField
            label="Hero Banner"
            type="banner"
            value={safeEventData.bannerImage}
            onChange={(previewUrl) => {
              const file = fileRefsRef.current.bannerImage;
              handleFileChange('bannerImage', previewUrl, file);
            }}
            onFileUpload={uploadImageToBackend}
            token={token}
            eventID={eventDetails?._id}
            hint="1920×1080px recommended for best quality"
            isUploading={uploadStatus.bannerImage === 'uploading'}
            uploadProgress={parseInt(uploadStatus.bannerImage?.split('-')[1] || 0)}
          />

          {/* Logo Upload */}
          <ImageUploadField
            label="Event Logo"
            type="logo"
            value={safeEventData.logoImage}
            onChange={(previewUrl) => {
              const file = fileRefsRef.current.logoImage;
              handleFileChange('logoImage', previewUrl, file);
            }}
            onFileUpload={uploadImageToBackend}
            token={token}
            eventID={eventDetails?._id}
            hint="PNG with transparency recommended"
            isUploading={uploadStatus.logoImage === 'uploading'}
            uploadProgress={parseInt(uploadStatus.logoImage?.split('-')[1] || 0)}
          />
        </div>
      </div>

      <div className="landing-edit-section landing-section-primary">
        <div className="landing-section-header">
          <div className="landing-section-header-content">
            <CheckCircle size={20} className="landing-section-icon" />
            <div>
              <h4 className="landing-section-title">What to Expect</h4>
              <p className="landing-section-description">Highlight key features of your event</p>
            </div>
          </div>
        </div>
        <div className="landing-form-group">
          <label className="landing-label">Event Features</label>
          
          {/* Existing features list */}
          {features.length > 0 && (
            <div className="landing-features-list" style={{ marginBottom: '16px' }}>
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="landing-feature-item"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '10px',
                    padding: '12px 14px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f1f5f9';
                    e.currentTarget.style.borderColor = '#cbd5e1';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8fafc';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                  }}
                >
                  <CheckCircle size={18} style={{ color: '#10b981', flexShrink: 0 }} />
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => handleUpdateFeature(index, e.target.value)}
                    className="landing-input"
                    style={{ flex: 1, margin: 0, border: 'none', background: 'transparent', padding: '4px 8px' }}
                    placeholder="Feature name"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveFeature(index)}
                    className="landing-feature-remove"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ef4444',
                      cursor: 'pointer',
                      padding: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      flexShrink: 0,
                      borderRadius: '4px',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#fee2e2';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    aria-label="Remove feature"
                  >
                    <X size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add new feature */}
          <div className="landing-feature-add" style={{ display: 'flex', gap: '10px', alignItems: 'stretch' }}>
            <input
              type="text"
              value={newFeature}
              onChange={(e) => setNewFeature(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddFeature();
                }
              }}
              className="landing-input"
              placeholder="Enter a feature (e.g., Keynote Speakers, Networking Sessions)"
              style={{ flex: 1 }}
            />
            <button
              type="button"
              onClick={handleAddFeature}
              disabled={!newFeature.trim() || features.includes(newFeature.trim())}
              className="landing-feature-add-btn"
              style={{
                padding: '10px 20px',
                backgroundColor: newFeature.trim() && !features.includes(newFeature.trim()) ? '#10b981' : '#d1d5db',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: newFeature.trim() && !features.includes(newFeature.trim()) ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: '600',
                fontSize: '14px',
                transition: 'all 0.2s ease',
                boxShadow: newFeature.trim() && !features.includes(newFeature.trim()) ? '0 2px 4px rgba(16, 185, 129, 0.2)' : 'none',
              }}
              onMouseEnter={(e) => {
                if (newFeature.trim() && !features.includes(newFeature.trim())) {
                  e.currentTarget.style.backgroundColor = '#059669';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(16, 185, 129, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (newFeature.trim() && !features.includes(newFeature.trim())) {
                  e.currentTarget.style.backgroundColor = '#10b981';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.2)';
                }
              }}
            >
              <Plus size={18} />
              Add Feature
            </button>
          </div>
          {features.length === 0 && (
            <div style={{ 
              marginTop: '12px', 
              padding: '16px', 
              backgroundColor: '#f8fafc', 
              borderRadius: '8px',
              border: '1px dashed #cbd5e1',
              textAlign: 'center'
            }}>
              <p style={{ fontSize: '0.875rem', color: '#64748b', margin: 0 }}>
                No features added yet. Add features to highlight what attendees can expect at your event.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="landing-edit-section landing-section-primary">
        <div className="landing-section-header">
          <div className="landing-section-header-content">
            <User size={20} className="landing-section-icon" />
            <div>
              <h4 className="landing-section-title">Speakers</h4>
              <p className="landing-section-description">Add speakers and their roles</p>
            </div>
          </div>
        </div>
        {(safeEventData.speakers || []).map((speaker, index) => {
          const bioLength = (speaker.bio || '').length;
          const maxBioLength = 500;
          
          return (
          <div key={index} style={{ marginBottom: '24px', padding: '20px', border: '1px solid #e2e8f0', borderRadius: '12px', backgroundColor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            {/* Speaker Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h5 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>
                Speaker {index + 1}
              </h5>
              <button
                type="button"
                onClick={() => {
                  const newSpeakers = (safeEventData.speakers || []).filter((_, i) => i !== index);
                  onUpdate('speakers', newSpeakers);
                }}
                style={{
                  padding: '4px 8px',
                  backgroundColor: 'transparent',
                  color: '#ef4444',
                  border: '1px solid #ef4444',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <X size={12} />
                Remove
              </button>
            </div>

            {/* Basic Info Row */}
            <div className="landing-form-row" style={{ marginBottom: '12px' }}>
              <div className="landing-form-group" style={{ flex: 1 }}>
                <label className="landing-label" style={{ marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#475569' }}>
                  Speaker Name *
                </label>
                <input
                  type="text"
                  className="landing-input"
                  placeholder="e.g., John Doe"
                  value={speaker.name || ''}
                  onChange={(e) => {
                    const newSpeakers = [...(safeEventData.speakers || [])];
                    newSpeakers[index] = { ...newSpeakers[index], name: e.target.value };
                    onUpdate('speakers', newSpeakers);
                  }}
                />
              </div>
              <div className="landing-form-group" style={{ flex: 1 }}>
                <label className="landing-label" style={{ marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#475569' }}>
                  Title/Role *
                </label>
                <input
                  type="text"
                  className="landing-input"
                  placeholder="e.g., CEO, Senior Developer"
                  value={speaker.title || ''}
                  onChange={(e) => {
                    const newSpeakers = [...(safeEventData.speakers || [])];
                    newSpeakers[index] = { ...newSpeakers[index], title: e.target.value };
                    onUpdate('speakers', newSpeakers);
                  }}
                />
              </div>
            </div>

            {/* Company Field */}
            <div className="landing-form-group" style={{ marginBottom: '12px' }}>
              <label className="landing-label" style={{ marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#475569' }}>
                Company/Organization
              </label>
              <input
                type="text"
                className="landing-input"
                placeholder="e.g., Tech Corp, University of XYZ"
                value={speaker.company || ''}
                onChange={(e) => {
                  const newSpeakers = [...(safeEventData.speakers || [])];
                  newSpeakers[index] = { ...newSpeakers[index], company: e.target.value };
                  onUpdate('speakers', newSpeakers);
                }}
              />
            </div>

            {/* Bio Textarea */}
            <div className="landing-form-group" style={{ marginBottom: '12px' }}>
              <label className="landing-label" style={{ marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Biography</span>
                <span style={{ fontSize: '0.75rem', color: bioLength > maxBioLength ? '#ef4444' : '#64748b', fontWeight: 400 }}>
                  {bioLength}/{maxBioLength} characters
                </span>
              </label>
              <textarea
                className="landing-input"
                placeholder="Write a brief biography about the speaker. This will be displayed in the speaker popup modal when visitors click on the speaker card."
                value={speaker.bio || ''}
                onChange={(e) => {
                  const newSpeakers = [...(safeEventData.speakers || [])];
                  newSpeakers[index] = { ...newSpeakers[index], bio: e.target.value };
                  onUpdate('speakers', newSpeakers);
                }}
                rows={4}
                maxLength={maxBioLength}
                style={{
                  width: '100%',
                  minHeight: '100px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  fontSize: '0.875rem',
                  lineHeight: '1.5',
                  padding: '10px 12px',
                  border: `1px solid ${bioLength > maxBioLength ? '#ef4444' : '#cbd5e1'}`,
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  transition: 'border-color 0.2s',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#f97316';
                  e.currentTarget.style.outline = 'none';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = bioLength > maxBioLength ? '#ef4444' : '#cbd5e1';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              <small className="landing-hint" style={{ display: 'block', marginTop: '6px', color: '#64748b', fontSize: '0.75rem' }}>
                Provide a brief overview of the speaker's background, expertise, and achievements.
              </small>
            </div>
            
            {/* Speaker Image Upload */}
            <div className="landing-form-group" style={{ marginTop: '12px' }}>
              <label className="landing-label" style={{ marginBottom: '8px', fontSize: '0.875rem', fontWeight: 500, color: '#475569' }}>Speaker Photo</label>
              {speaker.image && (
                <div style={{ marginBottom: '10px', position: 'relative', display: 'inline-block' }}>
                  <img
                    src={speaker.image}
                    alt="Speaker preview"
                    style={{
                      width: '100px',
                      height: '100px',
                      objectFit: 'cover',
                      borderRadius: '50%',
                      border: '2px solid #e2e8f0',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const newSpeakers = [...(safeEventData.speakers || [])];
                      newSpeakers[index] = { ...newSpeakers[index], image: '' };
                      onUpdate('speakers', newSpeakers);
                    }}
                    className="remove-image-btn"
                    style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: 'bold',
                    }}
                    aria-label="Remove speaker image"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
              <input
                type="file"
                id={`speaker-image-${index}`}
                accept="image/*"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    try {
                      // Validate speaker image
                      const fileErrors = validateImageFile(file, 'logo');
                      if (fileErrors.length > 0) {
                        console.warn('Speaker image validation errors:', fileErrors);
                        return;
                      }

                      const reader = new FileReader();
                      reader.onloadend = () => {
                        const newSpeakers = [...(safeEventData.speakers || [])];
                        newSpeakers[index] = { ...newSpeakers[index], image: reader.result };
                        onUpdate('speakers', newSpeakers);
                      };
                      reader.readAsDataURL(file);
                    } catch (error) {
                      console.error('Speaker image upload error:', error);
                    }
                  }
                }}
              />
              <label
                htmlFor={`speaker-image-${index}`}
                className="upload-label"
                style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  backgroundColor: speaker.image ? '#f1f5f9' : '#f97316',
                  color: speaker.image ? '#475569' : '#fff',
                  border: `1px solid ${speaker.image ? '#cbd5e1' : '#f97316'}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (speaker.image) {
                    e.currentTarget.style.backgroundColor = '#e2e8f0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (speaker.image) {
                    e.currentTarget.style.backgroundColor = '#f1f5f9';
                  }
                }}
              >
                <Upload size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                {speaker.image ? 'Change Photo' : 'Upload Photo'}
              </label>
              {!speaker.image && (
                <small className="landing-hint" style={{ display: 'block', marginTop: '4px', color: '#64748b', fontSize: '0.75rem' }}>
                  Square image recommended (e.g., 400x400px) for best display
                </small>
              )}
            </div>
          </div>
          );
        })}
        <button
          type="button"
          className="landing-button landing-button-secondary"
          onClick={() => {
            const currentSpeakers = safeEventData.speakers || [];
            onUpdate('speakers', [...currentSpeakers, { name: '', title: '', company: '', bio: '' }]);
          }}
          style={{ marginTop: '12px', width: '100%', padding: '12px', fontSize: '0.875rem', fontWeight: 600 }}
        >
          <Plus size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
          Add New Speaker
        </button>
      </div>

      <div className="landing-edit-section landing-section-primary">
        <div className="landing-section-header">
          <div className="landing-section-header-content">
            <Settings size={20} className="landing-section-icon" />
            <div>
              <h4 className="landing-section-title">Best Practices</h4>
              <p className="landing-section-description">Tips for creating an effective landing page</p>
            </div>
          </div>
        </div>
        <div className="landing-guidelines">
          <div className="landing-guideline-item">
            <strong>Image Quality</strong>
            <p>
              Use high-resolution images (at least 1920px wide) for best results on
              all devices. Max file size: 10MB for banner, 5MB for logo.
            </p>
          </div>
          <div className="landing-guideline-item">
            <strong>Content Length</strong>
            <p>
              Keep titles under 60 characters and descriptions under 200 characters
              for optimal display.
            </p>
          </div>
          <div className="landing-guideline-item">
            <strong>Brand Consistency</strong>
            <p>
              Ensure all colors and fonts align with your brand guidelines for
              professional appearance.
            </p>
          </div>
        </div>
      </div>

      {/* Event Configuration Section */}
      <div className="landing-config-section">
        <h3 className="landing-section-group-title">Event Configuration</h3>
        
        <CollapsibleSection
          title="Event Information"
          icon={<Type size={18} />}
          description="Update event name and description"
          defaultOpen={false}
        >
          {eventDetails && token && userID && (
            <EditInfo 
              userID={userID} 
              token={token} 
              eventDetails={eventDetails} 
            />
          )}
        </CollapsibleSection>

        <CollapsibleSection
          title="Event Time"
          icon={<Clock size={18} />}
          description="Set event dates and times"
          defaultOpen={false}
        >
          {eventDetails && token && userID && (
            <EditTime 
              userID={userID} 
              token={token} 
              eventDetails={eventDetails} 
            />
          )}
        </CollapsibleSection>

        {eventDetails?.eventFormat !== "Virtual" && (
          <CollapsibleSection
            title="Event Format & Venue"
            icon={<MapPin size={18} />}
            description="Configure event format and venue details"
            defaultOpen={false}
          >
            {eventDetails && token && userID && (
              <EventFormat
                userID={userID}
                token={token}
                eventDetails={eventDetails}
                eventID={eventDetails?._id}
                eventFormat={eventDetails?.eventFormat}
              />
            )}
          </CollapsibleSection>
        )}

        {/* ✅ UPDATED: Map Location with onUpdate callback */}
        {eventDetails?.eventFormat !== "Virtual" && (
          <CollapsibleSection
            title="Map Location"
            icon={<MapPin size={18} />}
            description="Add embedded Google Maps location"
            defaultOpen={false}
          >
            {eventDetails && token && userID && (
              <MapVenue
                userID={userID}
                token={token}
                eventDetails={eventDetails}
                // ✅ Use the onUpdate prop directly to sync venueMap
                onUpdate={(field, value) => {
                  if (field === 'venueMap') {
                    handleInputChange('venueMap', value);
                  }
                }}
              />
            )}
          </CollapsibleSection>
        )}

        <CollapsibleSection
          title="Event Visibility"
          icon={<Eye size={18} />}
          description="Control who can see your event"
          defaultOpen={false}
        >
          {eventDetails && token && userID && (
            <EventVisibility
              userID={userID}
              token={token}
              eventDetails={eventDetails}
            />
          )}
        </CollapsibleSection>

        <CollapsibleSection
          title="Event Support"
          icon={<Mail size={18} />}
          description="Manage contact information for event support"
          defaultOpen={false}
        >
          <p className="landing-hint" style={{ marginBottom: '12px' }}>
            Open the support editor to add or edit email and phone number for event support.
          </p>
          <button
            type="button"
            className="landing-button landing-button-primary"
            style={{
              backgroundColor: "#f97316",
              borderColor: "#f97316",
              color: "#fff"
            }}
            onClick={() => setIsSupportModalOpen(true)}
          >
            Manage Support
          </button>
        </CollapsibleSection>

        <CollapsibleSection
          title="Redirection After Registration"
          icon={<Link2 size={18} />}
          description="Set custom redirect URL after registration"
          defaultOpen={false}
        >
          {eventDetails && token && (
            <RedirectionLink
              token={token}
              eventDetails={eventDetails}
            />
          )}
        </CollapsibleSection>
      </div>

      {/* Event Branding Section */}
      <div className="landing-config-section">
        <h3 className="landing-section-group-title">Event Branding</h3>

        <CollapsibleSection
          title="Video Introduction"
          icon={<Video size={18} />}
          description="Add YouTube video URL for event introduction"
          defaultOpen={false}
        >
          {eventDetails && token && userID && (
            <EditVideoLink
              userID={userID}
              token={token}
              eventDetails={eventDetails}
            />
          )}
        </CollapsibleSection>

        <CollapsibleSection
          title="FAQ Section"
          icon={<HelpCircle size={18} />}
          description="Manage frequently asked questions"
          defaultOpen={false}
        >
          <p className="landing-hint" style={{ marginBottom: '12px' }}>
            Open the FAQ editor to add, edit or remove frequently asked questions for this event.
          </p>
          <button
            type="button"
            className="landing-button landing-button-primary"
            style={{
              backgroundColor: "#f97316",
              borderColor: "#f97316",
              color: "#fff"
            }}
            onClick={() => setIsFaqModalOpen(true)}
          >
            Manage FAQs
          </button>
        </CollapsibleSection>
      </div>

      {/* Sponsors management (moved from Invite Attendees) */}
      <div className="landing-edit-section landing-section-primary" >
        <div className="landing-section-header">
          <div className="landing-section-header-content">
            <Star size={20} className="landing-section-icon" />
            <div>
              <h4 className="landing-section-title">Sponsors</h4>
              <p className="landing-section-description">
                Manage event sponsors that will appear on your landing page
              </p>
            </div>
          </div>
        </div>
        <div className="landing-form-group">
          <p className="landing-hint" style={{ marginBottom: '12px' }}>
            Add or edit sponsors for this event. Logos and names will be shown on the landing page Sponsors section.
          </p>
          <button
            type="button"
            className="landing-button landing-button-primary"
            style={{
              backgroundColor: "#f97316",
              borderColor: "#f97316",
              color: "#fff"
            }}
            onClick={() => setIsSponsorsModalOpen(true)}
          >
            Manage Sponsors
          </button>
        </div>
      </div>

      {isFaqModalOpen && (
        <div
          className="landing-modal-overlay"
          onClick={() => setIsFaqModalOpen(false)}
        >
          <div
            className="landing-modal-content landing-modal-content-faq"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '900px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column',  marginLeft: '200px' }}
          >
            <div className="landing-modal-header">
              <div className="landing-modal-icon">
                <HelpCircle size={24} />
              </div>
              <h3>Edit Event FAQs</h3>
              <button
                className="landing-modal-close"
                onClick={() => setIsFaqModalOpen(false)}
                aria-label="Close FAQ editor"
              >
                <X size={20} />
              </button>
            </div>
            <div className="landing-modal-body landing-modal-body-scrollable">
              {eventDetails && token && (
                <Faq token={token} eventDetails={eventDetails} />
              )}
            </div>
          </div>
        </div>
      )}

      {isSponsorsModalOpen && (
  <div
    className="landing-modal-overlay"
    onClick={() => setIsSponsorsModalOpen(false)}
    style={{
      paddingLeft: '280px',  // ✅ Push entire overlay content right
    }}
  >
    <div
      className="landing-modal-content"
      onClick={(e) => e.stopPropagation()}
      style={{ 
        maxWidth: '900px',    // ✅ Slightly narrower for better fit
        width: 'calc(100% - 40px)', // ✅ Responsive width with padding
        margin: '40px auto',  // ✅ Center horizontally
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)', // ✅ Better shadow
      }}
    >
      <div className="landing-modal-header" style={{ flexShrink: 0, paddingTop: '40px', paddingBottom: '15px', paddingRight: '20px' }}>
        <div className="landing-modal-icon">
          <Star size={24} />
        </div>
        <h3>Manage Event Sponsors</h3>
        <button
          className="landing-modal-close" style={{ flexShrink: 0, paddingTop: '40px', paddingBottom: '15px', paddingRight: '20px' }}
          onClick={() => setIsSponsorsModalOpen(false)}
          aria-label="Close Sponsors editor"
        >
          <X size={20} />
        </button>
      </div>
      <div 
        className="landing-modal-body"
        style={{
          overflowY: 'auto',
          maxHeight: 'calc(90vh - 100px)',
          padding: '20px'
        }}
      >
        {eventDetails && token && (
          <Sponsors
            userID={userID}
            token={token}
            eventID={eventDetails?._id || eventDetails?.id}
          />
        )}
      </div>
    </div>
  </div>
)}
      {isSupportModalOpen && (
        <div
          className="landing-modal-overlay"
          onClick={() => setIsSupportModalOpen(false)}
        >
          <div
            className="landing-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '800px', width: '100%' }}
          >
            <div className="landing-modal-header">
              <div className="landing-modal-icon">
                <Mail size={24} />
              </div>
              <h3>Edit Event Support</h3>
              <button
                className="landing-modal-close"
                onClick={() => setIsSupportModalOpen(false)}
                aria-label="Close Support editor"
              >
                <X size={20} />
              </button>
            </div>
            <div className="landing-modal-body">
              {eventDetails && token && (
                <EventEmail
                  token={token}
                  eventDetails={eventDetails}
                />
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
});

CustomizePanel.displayName = 'CustomizePanel';

export default CustomizePanel;